import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { FileUploadServiceProvider } from '@services/files/file-upload-service/file-upload.service';
import { Observable } from 'rxjs/Observable';
import { ImageConverterProvider } from '@services/files/image-converter.service';
import { _throw } from 'rxjs/observable/throw';
import { MessageService } from '@services/helpers/message-service/message.service';
import { map } from 'rxjs/operators';

@Injectable()
export class FileUploadDataProvider {
	private monthNames: Array<string> = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December'
	];

	filesList: any = [];
	filesChanges: BehaviorSubject<any> = new BehaviorSubject([]);
	checkedFiles: BehaviorSubject<any> = new BehaviorSubject([]);
	fileToDelete: BehaviorSubject<any> = new BehaviorSubject(false);
	uploadedPhoto: BehaviorSubject<any> = new BehaviorSubject(null);
	newPhoto: BehaviorSubject<number> = new BehaviorSubject(null);

	private headers: HttpHeaders;

	constructor(
		private http: HttpClient,
		private fileUploadService: FileUploadServiceProvider,
		private imageConverter: ImageConverterProvider,
		private messageService: MessageService
	) {
		this.headers = new HttpHeaders({
			'Cache-Control': 'no-cache, no-store, must-revalidate',
			Expires: '0',
			Pragma: 'no-cache'
		});
	}

	get currentFileList(): Array<any> {
		return this.filesList;
	}

	get fileToDeleteObservable(): Observable<any> {
		return this.fileToDelete.asObservable();
	}

	get filesObservable(): Observable<any> {
		return this.filesChanges.asObservable();
	}

	get checkedFilesObservable(): Observable<any> {
		return this.checkedFiles.asObservable();
	}

	get uploadedPhoto$(): Observable<any> {
		return this.uploadedPhoto.asObservable();
	}

	get newPhoto$(): Observable<any> {
		return this.newPhoto.asObservable();
	}

	setFiles(files, isInitLoading): void {
		if (isInitLoading) {
			this.filesList = files;
		} else if (Array.isArray(files)) {
			this.filesList.push(...files);
		} else {
			this.filesList.push(files);
		}
		this.filesChanges.next(this.filesList);
	}

	setCheckedFiles(files): void {
		this.checkedFiles.next(files);
	}

	setFileToDelete(file): void {
		this.fileToDelete.next(file);
	}

	emitUploadedPhoto(photo: any): void {
		this.uploadedPhoto.next(photo);
	}

	emitNewPhoto(clientId: number): void {
		this.newPhoto.next(clientId);
	}

	saveFileOnServer(dataToSave, jobId): Observable<any> {
		if (typeof jobId !== 'undefined') {
			const dataToSend = new FormData();

			dataToSend.append('file', dataToSave);

			const url = `/rest/user/jobs/${jobId}/attachments/`;
			const headers: HttpHeaders = this.headers;

			return this.http
				.post(url, dataToSend, { headers })
				.map((response: any) => {
					response.convertedSize = (
						parseInt(response.bytesSize) /
						(1024 * 1024)
					).toFixed(2); // convert from bytes to MB
					const tmpDate = new Date(response.cratedTs);
					response.customLastModifiedDate = `${tmpDate.getDate()} ${
						this.monthNames[tmpDate.getMonth()]
					} ${tmpDate.getFullYear()}`;
					response.convertedType = this.imageConverter.getFileClass(
						response.name
					);
					return response;
				});
		}
	}

	sendFundyXmlFile(dataToSave, jobId): Observable<any> {
		if (jobId !== 'undefined') {
			const dataToSend = new FormData();

			dataToSend.append('data', dataToSave);

			const url = `/api/user/jobs/${jobId}/invoice/import/fundy`;
			const headers: HttpHeaders = this.headers;

			return this.http
				.post(url, dataToSend, { headers })
				.catch(error => this.handleXMlError(error.error));
		}
	}

	sendProSelectXmlFile(dataToSave, jobId): Observable<any> {
		if (jobId !== 'undefined') {
			const dataToSend = new FormData();

			dataToSend.append('data', dataToSave);

			const url = `/api/user/jobs/${jobId}/invoice/import/proselect`;
			const headers: HttpHeaders = this.headers;

			return this.http
				.post(url, dataToSend, { headers })
				.catch(error => this.handleProSelectUploadError(error.error));
		}
	}

	handleXMlError(error): any {
		if (error.errorTitle && error.errorTitle !== null) {
			this.messageService.showErrorFromBackend(
				error.errorTitle,
				error.errorMessage
			);
		} else {
			this.messageService.showErrorFromBackend('Not happy, Fundy!');
		}
		return _throw(error);
	}

	handleProSelectUploadError(error): any {
		if (error.errorTitle && error.errorTitle !== null) {
			this.messageService.showErrorFromBackend(
				error.errorTitle,
				error.errorMessage
			);
		} else {
			this.messageService.showErrorFromBackend('Incorrect, ProSelect!');
		}
		return _throw(error);
	}

	deleteFileFromServer(file, jobId): Observable<any> {
		const fileID = file.id;
		const url = `/rest/user/jobs/${jobId}/attachments/${fileID}`;
		const headers: HttpHeaders = this.headers;

		return this.http.delete(url, { headers }).pipe(map(data => {
			const index = this.filesList.indexOf(file);
			this.filesList.splice(index, 1);
			this.filesChanges.next(this.filesList);
			return data;
		}));
	}

	getAttachment(id, jobId): Observable<any> {
		const url = `/rest/user/jobs/${jobId}/attachments/${id}`;
		const headers: HttpHeaders = this.headers;
		headers.append('Content-Type', 'application/x-www-form-urlencoded');

		return this.http.get(url, { headers });
	}

	getAttachments(jobId): Observable<any> {
		const url = `/rest/user/jobs/${jobId}/attachments`;
		const headers: HttpHeaders = this.headers;
		headers.append('Content-Type', 'application/x-www-form-urlencoded');

		return this.http.get(url, { headers }).map((response: any) => {
			response.forEach(value => {
				value.convertedSize = (
					parseInt(value.bytesSize) /
					(1024 * 1024)
				).toFixed(2); // convert from bytes to MB
			});
		});
	}

	setAttachmentsFromServer(attachments): Array<any> {
		let attachmentsToShow = { ...attachments };
		attachmentsToShow = this.fileUploadService.convertAttachmentsToShow(
			attachments
		);
		// $rootScope.uploadedFiles = attachmentsToShow.length;
		this.setFiles(attachmentsToShow, true);
		return attachmentsToShow;
	}

	uploadClientPhoto(photo: any, clientId: number): Observable<any> {
		const url = `/api/user/clients/${clientId}/image`;
		const data = this.fileUploadService.base64StringToObject(photo);
		return this.http.put(url, data);
	}
	uploadUserPhoto(photo: any): Observable<any> {
		const url = '/api/user/v1/avatar';
		const data = this.fileUploadService.base64StringToObject(photo);
		return this.http.post(url, data);
	}
	getClientPhoto(clientId: number) {
		const url = `/api/user/clients/${clientId}/image`;
		return this.http.get(url);
	}
	getUserPhoto() {
		const url = '/api/user/v1/avatar';
		return this.http.get(url);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/files/file-upload.data.service.ts